import { zodResolver } from '@hookform/resolvers/zod'
import { DeleteOutline } from '@mui/icons-material'
import { DialogContent, DialogContentText } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import CustomDialog from '@/components/feedback/CustomDialog'
import FormDialogActions from '@/components/feedback/FormDialogActions'
import TextFieldController from '@/components/inputs/TextFieldController'
import { useAlertContext } from '@/contexts/AlertContext'
import { PrequalificationRouteInformation } from '@/features/prequalification/constants'
import { useDeleteLocalPqResourceSetMutation } from '@/features/prequalification/localControl/hooks/useDeleteLocalPqResourceSetMutation'
import { useInvalidateLocalPqResourceSetsQuery } from '@/features/prequalification/localControl/hooks/useLocalPqResourceSetsQuery'
import type { DeleteResourceSetFormValues } from '@/features/prequalification/localControl/schemas'
import { getDeleteResourceSetSchema } from '@/features/prequalification/localControl/schemas'
import { errorHandler } from '@/utils/errorHandler'

type DeleteResourceSetDialogProps = {
  resourceSetId: string
  resourceSetName: string
  onClose: () => void
}

/**
 * Permanently deletes a resource set from its detail page. Deletion is irreversible, so
 * the user must retype the resource set name to arm the action (the confirm button stays
 * disabled until the typed name matches). On success the list query is invalidated
 * and we navigate back to the index — the detail query is left to unmount rather
 * than invalidated, since refetching a just-deleted resource set would 404. On failure the
 * dialog stays open so the typed name is not lost and the delete can be retried.
 */
function DeleteResourceSetDialog({ resourceSetId, resourceSetName, onClose }: Readonly<DeleteResourceSetDialogProps>) {
  const { t } = useTranslation()
  const { pushAlert } = useAlertContext()
  const navigate = useNavigate()
  const { deleteResourceSet, isPending } = useDeleteLocalPqResourceSetMutation()
  const invalidateResourceSets = useInvalidateLocalPqResourceSetsQuery()

  const form = useForm<DeleteResourceSetFormValues>({
    mode: 'onChange',
    defaultValues: { confirmation: '' },
    resolver: zodResolver(getDeleteResourceSetSchema(t, resourceSetName)),
  })

  async function handleSubmit() {
    try {
      await deleteResourceSet(resourceSetId)
      await invalidateResourceSets()
      navigate(PrequalificationRouteInformation.INDEX.navigationPath())
      pushAlert({ message: t('prequalifications.resource_set.delete.success'), severity: 'success' })
    } catch (err) {
      const error = errorHandler(err, t('prequalifications.resource_set.delete.error'))
      pushAlert({ message: error.message, severity: 'error' })
    }
  }

  return (
    <CustomDialog open size="extraSmall" title={t('prequalifications.resource_set.delete.title')} onClose={onClose}>
      <FormProvider {...form}>
        <form noValidate onSubmit={form.handleSubmit(handleSubmit)}>
          <DialogContent>
            <DialogContentText color="error" sx={{ mb: 2 }}>
              {t('prequalifications.resource_set.delete.warning', { name: resourceSetName })}
            </DialogContentText>
            <TextFieldController
              required
              id="resource-set-delete-confirmation"
              label={t('prequalifications.resource_set.delete.confirm_label')}
              name="confirmation"
            />
          </DialogContent>

          <FormDialogActions
            isLoading={isPending}
            submitColor="error"
            submitDisabled={!form.formState.isValid}
            submitIcon={<DeleteOutline />}
            submitLabel={t('prequalifications.resource_set.delete.action')}
            onCancel={onClose}
          />
        </form>
      </FormProvider>
    </CustomDialog>
  )
}

export default DeleteResourceSetDialog
